import { getHTMLFromFragment, type Editor } from '@tiptap/react';
import type { Node } from '@tiptap/pm/model';
import { mapEntriesSize } from '@flintmint/core-shared/firestore';

import { CONTENT_SIZE_BUDGET, TEXT_BUDGET, documentByteLength } from '@/extensions/content-size-limit';
import { getEditorHTML } from '@/utils/editor-utils';

import type { PendingReview } from '../review/persisted-review';

// Agent writes bypass the editor's content size limit, so an agent run is checked once at its end,
// before anything is persisted: Firestore rejects a record over 1 MiB, and a rejected push never
// syncs. A run refused with this error reverts and ends as errored with reason `content_too_large`.
export class ContentTooLargeError extends Error {
  constructor() {
    super('Agent edit is over the content size budget');
    this.name = 'ContentTooLargeError';
  }
}

// A direct write saves the edited document as the record's content, under the same budget a user
// edit that adds text gets.
export function assertDocumentFitsTextBudget(doc: Node, html: string): void {
  if (documentByteLength(doc, html) > TEXT_BUDGET) {
    throw new ContentTooLargeError();
  }
}

// While a review is pending the record keeps the pre-run content next to `pendingReview`, which
// holds both documents again as JSON; accepting it saves the edited document as the content. The
// record is measured as Firestore stores it, where every node's map overhead, field names and
// numeric attributes cost more than their JSON text. The name and the other fields fit in the gap
// between the budget and Firestore's limit.
export function assertReviewFitsContentBudget({
  editor,
  beforeDoc,
  pendingReview,
}: {
  editor: Editor;
  beforeDoc: Node;
  pendingReview: PendingReview;
}): void {
  const reviewRecordSize = mapEntriesSize({
    content: getHTMLFromFragment(beforeDoc.content, beforeDoc.type.schema),
    parsedContent: beforeDoc.textBetween(0, beforeDoc.content.size, ' '),
    pendingReview,
  });
  if (reviewRecordSize > CONTENT_SIZE_BUDGET) {
    throw new ContentTooLargeError();
  }

  assertDocumentFitsTextBudget(editor.state.doc, getEditorHTML(editor));
}
